import React, { useRef } from 'react';
import { Box, Typography, Container, Grid, IconButton, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../ThemeContext';



// Icons for Services
import VideocamIcon from '@mui/icons-material/Videocam';
import PeopleIcon from '@mui/icons-material/People';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Icons for Bottom Features
import PublicIcon from '@mui/icons-material/Public';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BoltIcon from '@mui/icons-material/Bolt';
import StarBorderIcon from '@mui/icons-material/StarBorder';



const bottomFeatures = [
  { icon: <PublicIcon />, title: 'Global Community', desc: 'Join creators and fans from 100+ countries.' },
  { icon: <VerifiedUserIcon />, title: 'Verified & Secure', desc: 'Safe platform with verified profiles and content.' },
  { icon: <BoltIcon />, title: 'Fast & Reliable', desc: 'Smooth streaming and fast performance everywhere.' },
  { icon: <StarBorderIcon />, title: 'For Every Creator', desc: 'Film, Music, Art — every talent belongs here.' },
];

const CoreFeatures = () => {
  const { isDarkMode } = useTheme();

  const bgPrimary = isDarkMode ? '#050a14' : '#f8fafc';
  const cardBg = isDarkMode ? '#101726' : '#ffffff';
  const cardBorder = isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)';
  const textPrimary = isDarkMode ? '#ffffff' : '#0d1530';
  const textSecondary = isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';

  return (
    <Box sx={{ py: { xs: 8, md: 12 }, background: bgPrimary, position: 'relative', overflow: 'hidden' }}>
        {/* Glow Effects */}
        {isDarkMode && (
            <Box sx={{ position: 'absolute', top: '10%', right: '20%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(7, 164, 245,0.15) 0%, transparent 70%)', zIndex: 0 }} />
        )}
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
            

            {/* Media Services Header */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h3" sx={{ fontWeight: 800, color: textPrimary, fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
                    Explore Our <Box component="span" sx={{ color: '#07a4f5' }}>Media Services</Box>
                </Typography>
                <Typography sx={{ color: textSecondary, fontSize: '1.05rem', maxWidth: '600px', mx: 'auto' }}>
                    Powerful tools and platforms to stream, discover and grow your talent.
                </Typography>
            </Box>

            {/* Two Large Cards */}
            <Grid container spacing={4} sx={{ mb: 4 }}>
                {/* Film Streaming Card */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ background: cardBg, borderRadius: '24px', border: cardBorder, p: { xs: 3, md: 4 }, display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 4, height: '100%', alignItems: 'center', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' }, cursor: 'pointer' }}>
                        <Box sx={{ width: { xs: '100%', lg: '50%' }, height: 220, position: 'relative', borderRadius: '16px', overflow: 'hidden' }}>
                            <Box component="img" src="https://images.unsplash.com/photo-1616530940355-351fabd9524b?q=80&w=500&auto=format&fit=crop" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <Box sx={{ position: 'absolute', top: 12, left: 12, background: '#e11d48', color: 'white', px: 1.5, py: 0.5, borderRadius: '8px', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }} /> LIVE
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1, width: '100%' }}>
                            <Box sx={{ width: 48, height: 48, borderRadius: '50%', background: '#07a4f5', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                <VideocamIcon sx={{ color: 'white' }} />
                            </Box>
                            <Typography sx={{ color: textPrimary, fontWeight: 800, fontSize: '1.3rem', mb: 1.5 }}>Film Streaming</Typography>
                            <Typography sx={{ color: textSecondary, fontSize: '0.95rem', lineHeight: 1.6, mb: 3 }}>
                                Watch and enjoy a wide range of movies, web series, and exclusive shows in stunning quality.
                            </Typography>
                            <Button component={Link} to="/services/film-streaming" endIcon={<ArrowForwardIcon />} sx={{ color: '#07a4f5', textTransform: 'none', fontWeight: 700, p: 0, '&:hover': { background: 'transparent', textDecoration: 'underline' } }}>
                                Explore
                            </Button>
                        </Box>
                    </Box>
                </Grid>
                
                {/* Talent Discovery Card */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ background: cardBg, borderRadius: '24px', border: cardBorder, p: { xs: 3, md: 4 }, display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 4, height: '100%', alignItems: 'center', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' }, cursor: 'pointer' }}>
                        <Box sx={{ width: { xs: '100%', lg: '50%' }, height: 220, position: 'relative', borderRadius: '16px', overflow: 'hidden' }}>
                            <Box component="img" src="https://images.unsplash.com/photo-1478720568477-152d9b164e26?q=80&w=500&auto=format&fit=crop" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                        <Box sx={{ flex: 1, width: '100%' }}>
                            <Box sx={{ width: 48, height: 48, borderRadius: '50%', background: '#07a4f5', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                <PeopleIcon sx={{ color: 'white' }} />
                            </Box>
                            <Typography sx={{ color: textPrimary, fontWeight: 800, fontSize: '1.3rem', mb: 1.5 }}>Talent Discovery</Typography>
                            <Typography sx={{ color: textSecondary, fontSize: '0.95rem', lineHeight: 1.6, mb: 3 }}>
                                Discover and connect with amazing talent from around the world.
                            </Typography>
                            <Button component={Link} to="/services/talent-discovery" endIcon={<ArrowForwardIcon />} sx={{ color: '#07a4f5', textTransform: 'none', fontWeight: 700, p: 0, '&:hover': { background: 'transparent', textDecoration: 'underline' } }}>
                                Explore
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Bottom Features */}
            <Box sx={{ background: cardBg, borderRadius: '24px', border: cardBorder, p: 4 }}>
                <Grid container spacing={4}>
                    {bottomFeatures.map((feat, i) => (
                        <Grid item xs={12} sm={6} md={3} key={i}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', p: { xs: 0, md: 1 } }}>
                                <Box sx={{ width: 48, height: 48, borderRadius: '50%', background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: textSecondary, flexShrink: 0 }}>
                                    {feat.icon}
                                </Box>
                                <Box>
                                    <Typography sx={{ color: textPrimary, fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>{feat.title}</Typography>
                                    <Typography sx={{ color: textSecondary, fontSize: '0.85rem', lineHeight: 1.5 }}>{feat.desc}</Typography>
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

        </Container>
    </Box>
  );
};

export default CoreFeatures;
